import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import PdfViewerWithLocation from "../AIDocumentExtractors/PdfViewerWithLocation";
import { PsaFieldResult } from "../AIDocumentExtractors/types";
import Iconify from "../../minimals/components/iconify";
import { RecordablesAiAnalysisResponse } from "./types";

type DiffPart = {
  text: string;
  changed: boolean;
};

type DiffToken = {
  text: string;
  key: string;
};

const tokenizeForDiff = (value: string): DiffToken[] =>
  (value.match(/\s+|[^\s]+/g) ?? []).map((text) => ({
    text,
    key: /^\s+$/.test(text) ? " " : text
  }));

const mergeDiffParts = (parts: DiffPart[]): DiffPart[] =>
  parts.reduce<DiffPart[]>((merged, part) => {
    const previous = merged[merged.length - 1];
    if (previous?.changed === part.changed) {
      previous.text += part.text;
    } else {
      merged.push({ ...part });
    }
    return merged;
  }, []);

const buildDiffParts = (expectedValue: string, foundValue: string) => {
  const expected = tokenizeForDiff(expectedValue);
  const found = tokenizeForDiff(foundValue);
  const table = Array.from({ length: expected.length + 1 }, () => Array(found.length + 1).fill(0));

  for (let expectedIndex = expected.length - 1; expectedIndex >= 0; expectedIndex -= 1) {
    for (let foundIndex = found.length - 1; foundIndex >= 0; foundIndex -= 1) {
      table[expectedIndex][foundIndex] = expected[expectedIndex].key === found[foundIndex].key
        ? table[expectedIndex + 1][foundIndex + 1] + 1
        : Math.max(table[expectedIndex + 1][foundIndex], table[expectedIndex][foundIndex + 1]);
    }
  }

  const expectedParts: DiffPart[] = [];
  const foundParts: DiffPart[] = [];
  let expectedIndex = 0;
  let foundIndex = 0;

  while (expectedIndex < expected.length || foundIndex < found.length) {
    if (
      expectedIndex < expected.length &&
      foundIndex < found.length &&
      expected[expectedIndex].key === found[foundIndex].key
    ) {
      expectedParts.push({ text: expected[expectedIndex].text, changed: false });
      foundParts.push({ text: found[foundIndex].text, changed: false });
      expectedIndex += 1;
      foundIndex += 1;
    } else if (
      expectedIndex < expected.length &&
      (foundIndex >= found.length || table[expectedIndex + 1][foundIndex] >= table[expectedIndex][foundIndex + 1])
    ) {
      expectedParts.push({ text: expected[expectedIndex].text, changed: true });
      expectedIndex += 1;
    } else {
      foundParts.push({ text: found[foundIndex].text, changed: true });
      foundIndex += 1;
    }
  }

  return {
    expected: mergeDiffParts(expectedParts),
    found: mergeDiffParts(foundParts)
  };
};

const DiffText = ({ parts, side }: { parts: DiffPart[]; side: "expected" | "found" }) => (
  <>
    {parts.map((part, index) => (
      <Box
        component="span"
        key={`${side}-${index}`}
        sx={
          part.changed
            ? {
                color: side === "expected" ? "#1b5e20" : "#b71c1c",
                fontWeight: 800
              }
            : undefined
        }
      >
        {part.text}
      </Box>
    ))}
  </>
);

export default function RecordablesAiDrawer({
  open,
  loading,
  error,
  analysis,
  pdfUrl,
  onClose,
  onTaskAction
}: {
  open: boolean;
  loading: boolean;
  error?: string;
  analysis: RecordablesAiAnalysisResponse | null;
  pdfUrl?: string;
  onClose: () => void;
  onTaskAction: (taskItemId: number, status: number) => Promise<void>;
}) {
  const [activeTaskItemId, setActiveTaskItemId] = useState<number | null>(null);

  const issueFindings = useMemo(
    () =>
      (analysis?.findings ?? []).filter(
        (finding) => finding.recommendedStatus === "Missing/Incorrect" || finding.recommendedStatus === "Needs Confirmation"
      ),
    [analysis]
  );

  const activeFinding = useMemo(
    () => issueFindings.find((finding) => finding.taskItemId === activeTaskItemId) ?? null,
    [activeTaskItemId, issueFindings]
  );

  const activeField = useMemo<PsaFieldResult | null>(() => {
    if (!activeFinding) {
      return null;
    }

    const firstEvidenceWithPage = activeFinding.evidence.find((evidence) => evidence.pageNumber);

    return {
      key: String(activeFinding.taskItemId),
      label: activeFinding.taskDescription,
      value: activeFinding.summary,
      pageNumber: firstEvidenceWithPage?.pageNumber ?? null,
      sourceChunkId: null,
      sourceText: firstEvidenceWithPage?.sourceText ?? null,
      confidence: activeFinding.confidence,
      highlights: activeFinding.evidence.flatMap((evidence) => evidence.highlights)
    };
  }, [activeFinding]);

  const actionForTask = (taskItemId: number) =>
    (analysis?.taskActions ?? []).find((action) => action.taskItemId === taskItemId);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", lg: "94vw" },
          maxWidth: "100%"
        }
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              AI Recordable Review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {analysis
                ? analysis.openAiCallCount > 0 && issueFindings.length === 0
                  ? "No mismatches or missing data found"
                  : analysis.openAiCallCount > 0
                  ? `${issueFindings.length} item${issueFindings.length === 1 ? "" : "s"} need attention`
                  : "No AI-enabled checklist items to review"
                : "Document-backed checklist findings"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={onClose}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "hidden", p: 2 }}>
          {loading ? (
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: 280 }}>
              <CircularProgress />
              <Typography variant="subtitle2">Analyzing document, tasks, and order information...</Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !analysis ? (
            <Alert severity="info">Run the AI review to see task-by-task findings.</Alert>
          ) : (
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ height: "100%", minHeight: 0 }}>
              <Box sx={{ flex: "1 1 auto", minWidth: 0, height: "100%" }}>
                {pdfUrl ? (
                  <PdfViewerWithLocation pdfUrl={pdfUrl} activeField={activeField} />
                ) : (
                  <Alert severity="warning">The PDF could not be loaded for inline review.</Alert>
                )}
              </Box>

              <Stack
                spacing={1}
                sx={{
                  width: { xs: "100%", lg: 520 },
                  flex: "0 0 auto",
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 0.5
                }}
              >
                <Alert
                  severity="warning"
                  variant="filled"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 800,
                    fontSize: "0.75rem",
                    py: 0.75,
                    px: 1
                  }}
                >
                  Work in progress: AI findings are experimental and may be wrong or incomplete. Use this as a review
                  aid only; the recordable still needs normal human confirmation before any action is taken.
                </Alert>

                {analysis.warnings.map((warning) => (
                  <Alert key={warning} severity="warning">
                    {warning}
                  </Alert>
                ))}

                {analysis.openAiCallCount > 0 && issueFindings.length === 0 && (
                  <Alert severity="success">No mismatches or missing data were found for the required tasks.</Alert>
                )}

                {issueFindings.map((finding) => {
                  const isActive = activeTaskItemId === finding.taskItemId;
                  const selectedStatus = actionForTask(finding.taskItemId)?.status;
                  const hasSelectedAction = selectedStatus === 1 || selectedStatus === 2 || selectedStatus === 3;
                  const expectedValue = finding.expectedValue?.trim() ?? "";
                  const foundValue = finding.documentValue?.trim() ?? "";
                  const diff = buildDiffParts(expectedValue, foundValue);
                  const expectedParts = expectedValue
                    ? diff.expected
                    : [{ text: "Not available", changed: false }];
                  const foundParts = foundValue
                    ? diff.found
                    : [{ text: "Not found", changed: true }];

                  return (
                    <Paper
                      key={finding.taskItemId}
                      variant="outlined"
                      onClick={() => setActiveTaskItemId(finding.taskItemId)}
                      sx={{
                        borderRadius: 0.75,
                        overflow: "visible",
                        borderColor: isActive ? "primary.main" : "divider",
                        boxShadow: isActive
                          ? "0 0 0 1px rgba(25, 118, 210, 0.22), 0 3px 8px rgba(15, 23, 42, 0.10)"
                          : "0 3px 8px rgba(15, 23, 42, 0.06)",
                        cursor: "pointer",
                        backgroundColor: "#fff",
                        height: "auto"
                      }}
                      >
                        <Box
                        sx={{
                          backgroundColor: isActive ? "#f8fbff" : "#fff",
                          p: 1,
                          color: "text.primary"
                        }}
                        >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ pb: 0.5, mb: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ color: "text.primary", lineHeight: 1.2, fontWeight: 800, minWidth: 0 }}
                          >
                            {finding.taskDescription}
                          </Typography>
                          <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
                            <Tooltip
                              title={selectedStatus === 2 ? "Confirmed (click to reset)" : "Set Confirmed"}
                            >
                              <IconButton
                                size="small"
                                aria-label="Set task status to Confirmed"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void onTaskAction(finding.taskItemId, selectedStatus === 2 ? 0 : 2);
                                }}
                                sx={{ p: 0.25, display: hasSelectedAction && selectedStatus !== 2 ? "none" : "inline-flex" }}
                              >
                                <Iconify
                                  icon="eva:checkmark-fill"
                                  width={18}
                                  color="success.main"
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={selectedStatus === 1 ? "Missing/Incorrect (click to reset)" : "Set Missing/Incorrect"}
                            >
                              <IconButton
                                size="small"
                                aria-label="Set task status to Missing or Incorrect"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void onTaskAction(finding.taskItemId, selectedStatus === 1 ? 0 : 1);
                                }}
                                sx={{ p: 0.25, display: hasSelectedAction && selectedStatus !== 1 ? "none" : "inline-flex" }}
                              >
                                <Iconify
                                  icon="eva:close-fill"
                                  width={18}
                                  color="error.main"
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={selectedStatus === 3 ? "N/A (click to reset)" : "Set N/A"}>
                              <IconButton
                                size="small"
                                aria-label="Set task status to not applicable"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void onTaskAction(finding.taskItemId, selectedStatus === 3 ? 0 : 3);
                                }}
                                sx={{ p: 0.25, display: hasSelectedAction && selectedStatus !== 3 ? "none" : "inline-flex" }}
                              >
                                <Iconify
                                  icon="mingcute:na-line"
                                  width={18}
                                  color="#d89b00"
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 1 }}>
                          <Box
                            sx={{
                              minWidth: 0,
                              p: 0.75,
                              borderRadius: 0.75,
                              backgroundColor: "#fafafa",
                              overflowWrap: "anywhere"
                            }}
                          >
                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", lineHeight: 1, mb: 0.35 }}>
                              Expected
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.25 }}>
                              <DiffText parts={expectedParts} side="expected" />
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              minWidth: 0,
                              p: 0.75,
                              borderRadius: 0.75,
                              backgroundColor: "#fafafa",
                              overflowWrap: "anywhere"
                            }}
                          >
                            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", lineHeight: 1, mb: 0.35 }}>
                              Found
                            </Typography>
                            <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.25 }}>
                              <DiffText parts={foundParts} side="found" />
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
    </Drawer>
  );
}
